import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useUser } from '@clerk/clerk-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DocumentsContext = createContext(null);

export const DocumentsProvider = ({ children }) => {
    const { user } = useUser();
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastFetchTime, setLastFetchTime] = useState(null);

    const fetchDocuments = useCallback(async (force = false) => {
        if (!user) {
            setDocuments([]);
            setLoading(false);
            return;
        }

        // Skip if we fetched recently (within 5 seconds) and not forced
        const now = Date.now();
        if (!force && lastFetchTime && (now - lastFetchTime) < 5000) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await axios.get(`${API}/documents?user_id=${user.id}`);
            setDocuments(response.data);
            setLastFetchTime(Date.now());
        } catch (err) {
            console.error('Error fetching documents:', err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [user, lastFetchTime]);

    // Prefetch function for background loading (doesn't show loading state)
    const prefetchDocuments = useCallback(async () => {
        if (!user) return;

        // Skip if we fetched recently (within 5 seconds)
        const now = Date.now();
        if (lastFetchTime && (now - lastFetchTime) < 5000) {
            return;
        }

        try {
            const response = await axios.get(`${API}/documents?user_id=${user.id}`);
            setDocuments(response.data);
            setLastFetchTime(Date.now());
        } catch (err) {
            console.error('Error prefetching documents:', err);
        }
    }, [user, lastFetchTime]);

    // Refresh function for after uploads/deletes
    const refreshDocuments = useCallback(async () => {
        await fetchDocuments(true);
    }, [fetchDocuments]);

    // Initial fetch when user becomes available
    useEffect(() => {
        if (user) {
            fetchDocuments();
        }
    }, [user, fetchDocuments]);

    const value = {
        documents,
        loading,
        error,
        fetchDocuments,
        prefetchDocuments,
        refreshDocuments,
    };

    return (
        <DocumentsContext.Provider value={value}>
            {children}
        </DocumentsContext.Provider>
    );
};

export const useDocuments = () => {
    const context = useContext(DocumentsContext);
    if (!context) {
        throw new Error('useDocuments must be used within a DocumentsProvider');
    }
    return context;
};
